import "./MultiRatingTable.scss";

import { TextField } from "@mui/material";
import { MultiRatingItemDTO } from "api/news/__generated__/NewsApi";
import { SelectOption } from "appkit-react";
import Select from "components/Select";
import { useCallback, useMemo } from "react";

export type MultiRatingProps = {
  disabled?: boolean;
  onChange?: (value: MultiRatingItemDTO[]) => void;
  value?: MultiRatingItemDTO[];
};

const AVAILABLE_GROUPS = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));

const NEW_ROW: MultiRatingItemDTO = { id: "", subscriptionRatingTitle: "", ratingTitle: "", group: "", order: 0 };

export function MultiRatingTable({ disabled, onChange, value }: MultiRatingProps) {
const rows = useMemo(() => {
  const v = value ? [...value] : [];

  if (!v.length || (v[v.length - 1] && isValidMultiRating(v[v.length - 1]))) {
    v.push(NEW_ROW);
  }
  return v;
}, [value]);

  const handleRowChange = useCallback(
    (field: keyof MultiRatingItemDTO, index: number, newValue: string | number | undefined) => {
      const prev = value || [];
      const newRows = [
        ...prev.slice(0, Math.max(0, index)),
        { ...prev[index], [field]: newValue },
        ...prev.slice(index + 1),
      ];
      onChange?.(newRows);
    },
    [onChange, value]
  );

  const handleRemoveRow = useCallback(
    (index: number) => {
      const prev = value || [];
      const newRows = [...prev.slice(0, Math.max(0, index)), ...prev.slice(index + 1)];
      onChange?.(newRows);
    },
    [onChange, value]
  );

  return (
    <div className={`multi-rating-table ${disabled ? "disabled" : ""}`}>
      <table>
        <thead>
          <tr>
            <th></th>
            <th>Subscription title</th>
            <th>Rating title</th>
            <th>Group</th>
            <th>Order</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows?.map((row, i) => (
            <tr key={row.id || i}>
              <td className="position">
                {String(1 + i).padStart(String(rows.length).length, "0")}
                {}
              </td>
              <td>
                <div className="input-with-helper">
                  <TextField
                    className="multi-rating-field"
                    value={row.subscriptionRatingTitle}
                    onChange={(e) => handleRowChange("subscriptionRatingTitle", i, e.target.value)}
                    disabled={disabled}
                  />
                  <div className="helper-text">{'\u00A0'}</div>
                </div>
              </td>
              <td>
              <div className="input-with-helper">
                <TextField
                  className="multi-rating-field"
                  value={row.ratingTitle}
                  onChange={(e) => handleRowChange("ratingTitle", i, e.target.value)}
                  disabled={disabled}
                  error={!row.ratingTitle?.trim()}
                />
                <div className="helper-text">{!row.ratingTitle?.trim() ? "Rating title is required" : "\u00A0"}</div>
              </div>
              </td>
              <td>
                <GroupField
                  value={row.group || ""}
                  onChange={(value) => handleRowChange("group", i, value || "")}
                  disabled={disabled}
                  error={!row.group?.trim()}
                  helperText={!row.group?.trim() ? "Group is required" : "\u00A0"}
                />
              </td>
              <td>
                <OrderField
                  value={row.order}
                  onChange={(value) => handleRowChange("order", i, value)}
                  disabled={disabled}
                  error={row.order === undefined || isNaN(row.order)}
                  helperText="Order is required"
                />
              </td>
              <td className="delete">
                {i < rows.length - 1 && (
                  <button className="delete-btn" onClick={() => handleRemoveRow(i)}>
                    <span className="appkiticon icon-circle-delete-outline clear-button" />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function isValidMultiRating(r: MultiRatingItemDTO) {
  return (
    !!r.ratingTitle?.trim() &&
    !!r.group?.trim() && 
    typeof r.order === "number" && !isNaN(r.order)
  );
}
export type GroupFieldProps = {
  value?: string;
  disabled?: boolean;
  label?: React.ReactNode;
  onChange: (value: string | undefined) => void;
  error?: boolean;
  helperText?: React.ReactNode;
};
export function GroupField({ onChange, disabled, value, label,error, helperText }: GroupFieldProps) {
  const handleChange = useCallback((val: string) => onChange(val), [onChange]);

  const field = (
        <div className={`group-field-container ${error ? "error" : ""}`}>
    <Select className="multi-rating-field field-group" value={value} onSelect={handleChange} disabled={disabled}>
      {AVAILABLE_GROUPS.map((g) => (
        <SelectOption key={g} value={g}>
          {g}
        </SelectOption>
      ))}
    </Select>
     <div className="helper-text">{error && helperText ? helperText : "\u00A0"}</div>
    </div>
  );

  if (!label) return field;
  return (
    <label className="multi-rating-field-label field-group">
      {label} {field}
    </label>
  );
}

export type OrderFieldProps = {
  value?: number;
  disabled?: boolean;
  label?: React.ReactNode;
  onChange: (value: number | undefined) => void;
  error?: boolean;
  helperText?: React.ReactNode;
};
export function OrderField({ onChange, disabled, value, label,error,helperText }: OrderFieldProps) {
  const handleChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = useCallback(
    (e) => {
      onChange(!e.target.value ? undefined : parseInt(e.target.value));
    },
    [onChange]
  );

  const field = (
  <div className="order-field-container">
    <TextField
      className="multi-rating-field field-order"
      type="number"
      value={value}
      onChange={handleChange}
      disabled={disabled}
      error={error}
      helperText={error && helperText ? helperText : "\u00A0"}
      size="small"
    />
  </div>
  );
  if (!label) return field;

  return (
    <label className="multi-rating-field-label field-order">
      {label} {field}
    </label>
  );
}
